import { mainApi } from '../../../../api/mainApi';
import { middlewareApi } from '../../../middleware/middlewareApi';

import { setPeriodTypesList } from '../slices/periodTypesSlice';

import {
  PeriodTypesResponse,
  PeriodType,
  PeriodTypeAvailable,
} from '../interfaces/period-types.interfaces';

export const periodTypesTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['PeriodTypes', 'PeriodTypesAvailables'],
});
// Define a service using a base URL and expected endpoints
export const periodTypesApi = periodTypesTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all period types
     */
    getPeriodTypes: builder.query<
      PeriodTypesResponse,
      {
        profileId: number;
        page: number;
        perPage: number;
        search: string;
        sort?: string;
        type_sort?: string;
        tp_name?: string;
      }
    >({
      queryFn: async (
        { profileId, page, search, perPage, sort, type_sort, tp_name },
        { dispatch }
      ) => {
        try {
          const { data } = await mainApi.get<PeriodTypesResponse>('/type-periods', {
            params: {
              user_profile_id: profileId,
              page,
              size: perPage,
              type_sort: type_sort || 'desc',
              ...(sort && { sort }),
              search,
              tp_name,
            },
          });

          dispatch(setPeriodTypesList(data.data));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'PeriodTypes', id } as const)),
              { type: 'PeriodTypes', id: 'LIST' },
            ]
          : [{ type: 'PeriodTypes', id: 'LIST' }],
    }),

    /**
     * @GET period types to combo box
     */
    getAvailablePeriodTypes: builder.query<PeriodTypeAvailable[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get('/type-periods', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'PeriodTypesAvailables', id: 'COMBO_LIST' }],
    }),

    /**
     * @GET period types to combo box
     */
    getAvailablePeriodTypesAdditional: builder.query<PeriodTypeAvailable[], { profileId: number }>({
      queryFn: async ({ profileId }) => {
        try {
          const { data } = await mainApi.get('/type-periods/additional/fees', {
            params: {
              user_profile_id: profileId,
              data: 'all',
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'PeriodTypesAvailables', id: 'COMBO_LIST' }],
    }),

    /**
     * @GET period type by id
     */
    getPeriodType: builder.query<PeriodType, { profileId: number; periodTypeId: number }>({
      queryFn: async ({ profileId, periodTypeId }) => {
        try {
          const { data } = await mainApi.get<PeriodType>(`/type-periods/${periodTypeId}`, {
            params: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result, error, { profileId }) => [{ type: 'PeriodTypes', profileId }],
    }),

    /**
     * @POST create period type
     */
    addPeriodType: builder.mutation<
      PeriodType,
      { profileId: number; periodPayload: Partial<PeriodType> }
    >({
      queryFn: async ({ profileId, periodPayload }) => {
        try {
          const { data } = await mainApi.post<PeriodType>('/type-periods', {
            user_profile_id: profileId,
            ...periodPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'PeriodTypes', id: 'LIST' },
        { type: 'PeriodTypesAvailables', id: 'COMBO_LIST' },
      ],
    }),

    /**
     * @PUT update period
     */
    updatePeriodType: builder.mutation<
      PeriodType,
      { profileId: number; periodTypeId: number; periodPayload: Partial<PeriodType> }
    >({
      queryFn: async ({ profileId, periodTypeId, periodPayload }) => {
        try {
          const { data } = await mainApi.put<PeriodType>(`/type-periods/${periodTypeId}`, {
            user_profile_id: profileId,
            ...periodPayload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'PeriodTypes', id: 'LIST' },
        { type: 'PeriodTypesAvailables', id: 'COMBO_LIST' },
      ],
    }),

    /**
     * @DELETE remove period
     */
    deletePeriodType: builder.mutation<PeriodType, { profileId: number; periodTypeId: number }>({
      queryFn: async ({ profileId, periodTypeId }) => {
        try {
          const { data } = await mainApi.delete<PeriodType>(`type-periods/${periodTypeId}`, {
            data: {
              user_profile_id: profileId,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [
        { type: 'PeriodTypes', id: 'LIST' },
        { type: 'PeriodTypesAvailables', id: 'COMBO_LIST' },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPeriodTypeQuery,
  useGetPeriodTypesQuery,
  useAddPeriodTypeMutation,
  useUpdatePeriodTypeMutation,
  useDeletePeriodTypeMutation,
  useGetAvailablePeriodTypesQuery,
  useGetAvailablePeriodTypesAdditionalQuery,
} = periodTypesApi;
